import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicAuthLayout, { PublicAuthAlert, PublicAuthSpinner } from '../components/PublicAuthLayout';
import { resetPassword } from '../services/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'This reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicAuthLayout
      asideBody="Choose a fresh password for your Seedronics account. Once it is saved, older browser sessions are signed out automatically."
      asideTitle={done ? 'Your password is updated.' : 'Create a new password.'}
      eyebrow="Secure reset"
      footer={
        <>
          Need a new link? <Link to="/forgot-password">Request another reset</Link>
        </>
      }
      highlights={['Minimum 8 characters', 'Old sessions revoked', 'Secure account recovery']}
      title={done ? 'You can sign in again.' : 'Set a new Seedronics password.'}
      subtitle={done ? 'Use your new password the next time you open the console.' : 'Enter and confirm the password you want to use going forward.'}
    >
      {!token && (
        <PublicAuthAlert>
          <span>Invalid reset link. The token is missing.</span>
          <Link to="/forgot-password">Request a new link</Link>
        </PublicAuthAlert>
      )}

      {error && <PublicAuthAlert>{error}</PublicAuthAlert>}

      {done ? (
        <Link className="sd-public-auth-submit" to="/login">
          Sign in with new password
        </Link>
      ) : token ? (
        <form className="sd-public-auth-form" onSubmit={handleSubmit}>
          <label>
            New password
            <input
              autoComplete="new-password"
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Minimum 8 characters"
              required
              type="password"
              value={password}
            />
          </label>
          <label>
            Confirm password
            <input
              autoComplete="new-password"
              minLength={8}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Re-enter password"
              required
              type="password"
              value={confirmPassword}
            />
          </label>
          <button className="sd-public-auth-submit" disabled={loading} type="submit">
            {loading ? <PublicAuthSpinner /> : 'Reset password'}
          </button>
        </form>
      ) : null}
    </PublicAuthLayout>
  );
}
